import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Circle, GoogleMap, InfoWindow, Marker, useJsApiLoader, MarkerClusterer } from '@react-google-maps/api';
import './style.css';
import CustomMarker from '../../Atoms/CustomMarker';
import { calculateBearing } from '../../../Library/geodist';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_VERSION } from '../../../Library/constants';
import { getItemFromLocalStorage, tsWithin10Mins } from '../../../Library/helpers';
import { Button, Checkbox, Tooltip } from '@chakra-ui/react';
import { TargetIcon, ZoomOutIcon } from '../../../Assets/images/svgs';
import { StoreContext } from '../../../Library/contexts';

const GeofenceMarker = require('../../../Assets/images/pngs/GeofenceMarker.png');
const GEOFENCE_MARKER = { "url": GeofenceMarker };

const libraries = ["places", "drawing", "geometry"];
const VehicleMap = ({ vids = [], vehMap = new Map(), allGeofences = [], updateVehiclesTable, showExistingGfsToggle = false, setShowExistingGeofences, showExistingGeofences, hideGeofenceMarkers, isStateFilterActive, mapContainerClass, mappedDevPage, isSellerOrDealer }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    version: GOOGLE_MAPS_VERSION,
  });

  // const [vehicleMap, setVehicleMap] = useState(new Map());
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const vehicleMapRef = useRef(new Map());
  const [showVehicleZoom, setShowVehicleZoom] = useState({});
  const [isVehicleFocused, setIsVehicleFocused] = useState(false);
  const { ltLastSeenData, setLtLastSeenData } = useContext(StoreContext);
  const initialMapBounds = useRef({});
  const lastSeenData = useRef([]);

  const mapRef = useRef(null);
  const [clusterMaxZoom, setClusterMaxZoom] = useState(null);
  const isUser = useRef(getItemFromLocalStorage());
  const originalVehicleMap = useRef([]);
  const [mapCenter, setMapCenter] = useState({ lat: 23.2599, lng: 77.4126 });
  const [cachedVehicleMap, setCachedVehicleMap] = useState(new Map());
  const vehsToUpdateInMap = useRef(new Set());
  const updateMapRef = useRef(true);
  const subscribedVeh = useRef(null);

  const visibleData = useRef([]);
  const dynamicTimeoutRef = useRef(null);
  const preventOnZoomRef = useRef(null);
  const isDataStoredInContext = useRef(false);

  useEffect(() => {
    if (!Array.from(vehicleMapRef.current.values()).length || !Array.from(vehMap.values()).length) return;

    if (!originalVehicleMap.current || !originalVehicleMap.current.size) originalVehicleMap.current = new Map(vehicleMapRef.current);
    let newVehicleMap = new Map(vehicleMapRef.current);

    Array.from(originalVehicleMap.current.values()).forEach(vehicle => {
      let vehicleValue = vehMap.get(vehicle.vid);
      if (!vehicleValue || !vehicleValue.idx) newVehicleMap.delete(vehicle.vid)
      else {
        let newVehicleMapValue = newVehicleMap.get(vehicle.vid)
        if (!newVehicleMapValue || !newVehicleMapValue.vid) newVehicleMap.set(vehicle.vid, originalVehicleMap.current.get(vehicle.vid));
      }
    });

    // setVehicleMap(newVehicleMap);
    setCachedVehicleMap(newVehicleMap);
    vehicleMapRef.current = newVehicleMap;
  }, [vehMap]);

  useEffect(() => {
    let vehicles = Array.from(vehMap.values());
    if (isStateFilterActive && vehicles && vehicles.length) {
      var bounds = new window.google.maps.LatLngBounds();
      vehicles.forEach(vehicle => {
        if (!vehicle.ping) vehicle.ping = vehicleMapRef.current.get(vehicle.idx);
        if (vehicle.ping && vehicle.ping.lat && vehicle.ping.lng) {
          var latLng = new window.google.maps.LatLng(parseFloat(vehicle.ping.lat, 10), parseFloat(vehicle.ping.lng, 10));
          bounds.extend(latLng);
        }
      });

      if (mapRef.current) {
        mapRef.current.fitBounds(bounds);
        let newCenter = bounds.getCenter();

        var centerLatLng = new window.google.maps.LatLng(parseFloat(newCenter.lat(), 10), parseFloat(newCenter.lng(), 10));
        mapRef.current.setCenter(centerLatLng);
      }
    }

  }, [isStateFilterActive, vehMap]);

  const syncCachedMapMarkerWithLatest = (veh) => {
    vehsToUpdateInMap.current.add(veh.vid);
    setCachedVehicleMap((prev) => {
      const newMap = new Map(prev);
      const vehDet = vehicleMapRef.current.get(veh.vid);
      if (vehDet) newMap.set(veh.vid, vehDet);
      return newMap;
    });
  };

  const handleHideVehicleInfo = (veh) => {
    vehsToUpdateInMap.current.delete(veh.vid);
  }

  const handleVehicleZoom = (veh) => {
    preventOnZoomRef.current = true;
    handleCustMarkerClick(veh.vid);

    if (veh.zoom) handleVehicleZoomIn(veh.lat, veh.lng);
    setShowVehicleZoom(veh);
    syncCachedMapMarkerWithLatest(veh);
  }

  const updateClusterMaxZoom = () => {
    const map = mapRef.current;
    if (!map) return;
    const mapType = map.getMapTypeId();
    if (mapType !== 'satellite' && mapType !== 'hybrid') return setClusterMaxZoom(null);
    new window.google.maps.MaxZoomService().getMaxZoomAtLatLng(map.getCenter(), ({ status, zoom }) => {
      setClusterMaxZoom(status === 'OK' ? zoom - 1 : null);
    });
  };

  const onLoad = (map) => {
    mapRef.current = map;
    if (lastSeenData.current && lastSeenData.current.length) handleLastSeen(lastSeenData.current);
  };

  const handleLastSeen = useCallback((data) => {
    if (!mapRef.current) {
      lastSeenData.current = data;
      return;
    }

    let totalLat = 0;
    let totalLng = 0;

    data.forEach((vehicle) => {
      totalLat += vehicle.lat;
      totalLng += vehicle.lng;
      addUpdateValueInVehicleMap(vehicle.vid, vehicle);
    });

    var bounds = new window.google.maps.LatLngBounds();
    let lastSeenVehMap = new Map(vehicleMapRef.current);
    data.forEach((item) => {
      if (item && item.lat && item.lng) {
        if (!item.ignition || !tsWithin10Mins(item.ts)) item.spd = 0;
        if (item.brg) item.direction = item.brg;

        var latLng = new window.google.maps.LatLng(parseFloat(item.lat, 10), parseFloat(item.lng, 10));
        if (latLng) bounds.extend(latLng);

        if (data.length > 1) {
          mapRef.current.initialZoom = true;
          if (updateMapRef.current) mapRef.current.fitBounds(bounds);
          initialMapBounds.current = bounds;
        }
      }

      lastSeenVehMap.set(item.vid, item);
    });
    // setVehicleMap(lastSeenVehMap);
    if (updateMapRef.current) setCachedVehicleMap(lastSeenVehMap);
    vehicleMapRef.current = addVehInStats(lastSeenVehMap);
    // if (updateVehiclesTable) updateVehiclesTable(lastSeenVehMap);
    if (!ltLastSeenData && !isDataStoredInContext.current) {
      setLtLastSeenData(data);
      isDataStoredInContext.current = true;
    }
    global.EE.emitEvent(global.EE.EVENT_TYPES.LIVE_TRACKING_VEH_STATS, vehicleMapRef.current);

    if (data.length > 1) {
      window.google.maps.event.addListener(mapRef.current, 'zoom_changed', function () {
        let zoomChangeBoundsListener =
          window.google.maps.event.addListener(mapRef.current, 'bounds_changed', function () {
            if (mapRef.current.getZoom() > 15 && mapRef.current.initialZoom === true) {
              mapRef.current.setZoom(16);
              mapRef.current.initialZoom = false;
            }
            window.google.maps.event.removeListener(zoomChangeBoundsListener);
          });
      });
    }

    if (totalLat && totalLng) {
      let centerLat = totalLat / data.length;
      let centerLng = totalLng / data.length;
      let center = { lat: centerLat, lng: centerLng };

      if (updateMapRef.current) setMapCenter(center);
    }
  }, [vids]);

  const addVehInStats = (stats) => {
    const newVehStats = new Map();

    stats.forEach((stat, vid) => {
      const veh = vehMap.get(vid);
      newVehStats.set(vid, { ...stat, veh: veh || stat.veh });
    });

    return newVehStats;
  }

  const updateVehicleMap = useCallback((data) => {
    if (data && data.vid) {
      const vehicle = vehicleMapRef.current.get(data.vid);
      if (!vehicle) return;

      if (data.ignition && data.spd >= 5) {
        if (!data.brg) {
          data.direction = calculateBearing(vehicle.lat, vehicle.lng, data.lat, data.lng);
        } else {
          data.direction = data.brg;
        }
      }

      if (vehicle.heartbeat) {
        data.heartbeat = vehicle.heartbeat;
      }

      vehicleMapRef.current.set(data.vid, data);
      vehicleMapRef.current = addVehInStats(vehicleMapRef.current);
      // if (updateVehiclesTable) updateVehiclesTable(new Map(vehicleMapRef.current));
      global.EE.emitEvent(global.EE.EVENT_TYPES.LIVE_TRACKING_VEH_STATS, vehicleMapRef.current);

      if (data.vid && vehsToUpdateInMap.current.size && vehsToUpdateInMap.current.has(data.vid)) {
        setCachedVehicleMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(data.vid, data);
          return newMap;
        });
      }

      if (vids.length === 1 && data.lat && data.lng) {
        const latLng = new window.google.maps.LatLng(parseFloat(data.lat, 10), parseFloat(data.lng, 10));
        mapRef.current?.setCenter(latLng);
        mapRef.current?.setZoom(16);
      }
    }
  }, [vids]);

  const handleGetLastSeen = (bikesAndLastSeen) => {
    let action = (isUser.current || isSellerOrDealer) ? "getDynamicLastSeen" : "getLastSeen";
    let data = { vids: (!isDataStoredInContext.current && !visibleData.current.length) ? vids : visibleData.current };
    if (mappedDevPage && global.WS && global.WS.wsConnected) {
      global.WS.sendRequest({ action, data });
    } else if (global.UWS && global.UWS.wsConnected) {
      clearTimeout(dynamicTimeoutRef.current);
      dynamicTimeoutRef.current = setTimeout(() => {
        updateMapRef.current = false;
        handleGetLastSeen();
      }, 30000);

      if (!bikesAndLastSeen || !bikesAndLastSeen.lastseen || !bikesAndLastSeen.lastseen.length) global.UWS.sendRequest({ action, data });
      else {
        lastSeenData.current = bikesAndLastSeen.lastseen;
        setLtLastSeenData(bikesAndLastSeen.lastseen);
        handleLastSeen(bikesAndLastSeen.lastseen);
      }
    }
  }

  const initListeners = useCallback(() => {
    let wsKey = "UWS";
    let event = global.EE.EVENT_TYPES.UWS_CONNECTED;
    if (mappedDevPage) {
      wsKey = "WS";
      event = global.EE.EVENT_TYPES.WS_CONNECTED;
    }
    if (!global[wsKey] || !global[wsKey].wsConnected) {
      const onWsConnected = () => {
        global.EE.removeEventListener(event, onWsConnected);
        initListeners();
      }

      global.EE.addEventListener(event, onWsConnected);
      return;
    }

    // Add Listeners for Last Seen and Pings
    global.EE.addEventListener(global.EE.EVENT_TYPES.ON_LAST_SEEN, handleLastSeen);
    global.EE.addEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, updateVehicleMap);

    // Subscribe to Given VIDs
    if (ltLastSeenData) {
      handleLastSeen(ltLastSeenData);
      dynamicTimeoutRef.current = setTimeout(() => {
        updateMapRef.current = false;
        handleGetLastSeen();
      }, 30000);
    } else if (lastSeenData.current) handleLastSeen(lastSeenData.current)
  }, [vids, handleLastSeen, updateVehicleMap]);

  useEffect(() => {
    initListeners();
    global.EE.addEventListener(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, handleVehicleZoom);
    global.EE.addEventListener(global.EE.EVENT_TYPES.HIDE_VEHICLE_INFO, handleHideVehicleInfo);
    global.EE.addEventListener(global.EE.EVENT_TYPES.ON_BIKES, handleGetLastSeen);
    global.EE.addEventListener(global.EE.EVENT_TYPES.LT_DATA_SORTED, updateVisibleData);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, handleVehicleZoom);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.HIDE_VEHICLE_INFO, handleHideVehicleInfo);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_BIKES, handleGetLastSeen);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.LT_DATA_SORTED, updateVisibleData);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_LAST_SEEN, handleLastSeen);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, updateVehicleMap);

      clearTimeout(dynamicTimeoutRef.current);

      const action = global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES;
      let unsubVeh = [];

      if (subscribedVeh.current) unsubVeh.push(subscribedVeh.current);
      if (unsubVeh.length) {
        if (mappedDevPage) {
          if (global.WS && global.WS.wsConnected) global.WS.sendRequest({ action, data: unsubVeh });
        } else if (global.UWS && global.UWS.wsConnected) global.UWS.sendRequest({ action, data: unsubVeh });
      }
    }
  }, []);

  const updateVisibleData = (data) => {
    // visibleData.current = data.map(veh => veh.idx);
  }

  const addUpdateValueInVehicleMap = (key, value) => {
    // setVehicleMap((prevMap) => new Map(prevMap.set(key, value)));
    vehicleMapRef.current.set(key, value);
    if (updateMapRef.current) setCachedVehicleMap(new Map(vehicleMapRef.current));
  };

  const onZoomChanged = () => {
    if (preventOnZoomRef.current) {
      preventOnZoomRef.current = false;
      return;
    }

    if (vehicleMapRef.current?.size === 1) {
      vehicleMapRef.current?.forEach((vehicle) => {
        if (!vehicle) return;

        var latLng = new window.google.maps.LatLng(parseFloat(vehicle.lat, 10), parseFloat(vehicle.lng, 10));
        mapRef.current.setCenter(latLng);
      });
    }

    if (vehicleMapRef.current?.size > 1) {
      var bounds = new window.google.maps.LatLngBounds();
      vehicleMapRef.current?.forEach((vehicle) => {
        if (vehicle && vehicle.lat && vehicle.lng) {
          var latLng = new window.google.maps.LatLng(parseFloat(vehicle.lat, 10), parseFloat(vehicle.lng, 10));
          if (latLng) bounds.extend(latLng);
        }
      });
    }
  };

  const handleVehicleZoomIn = (lat, lng) => {
    if (isVehicleFocused) {
      mapRef.current.fitBounds(initialMapBounds.current);
      setIsVehicleFocused(false);
      setShowVehicleZoom(false);
      let unsubVids = [subscribedVeh.current];
      subscribedVeh.current = null;
      global.UWS.sendRequest({ action: global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES, data: { veh: unsubVids } });
      return;
    }

    var bounds = new window.google.maps.LatLngBounds();
    var latLng = new window.google.maps.LatLng(parseFloat(((showVehicleZoom && showVehicleZoom.lat) ? showVehicleZoom.lat : lat), 10), parseFloat(((showVehicleZoom && showVehicleZoom.lng) ? showVehicleZoom.lng : lng), 10));
    bounds.extend(latLng);
    mapRef.current.fitBounds(bounds);

    setIsVehicleFocused(true);
  }

  const generateClusterIcon = (color, size) => {
    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M28 40.25C34.7655 40.25 40.25 34.7655 40.25 28C40.25 21.2345 34.7655 15.75 28 15.75C21.2345 15.75 15.75 21.2345 15.75 28C15.75 34.7655 21.2345 40.25 28 40.25Z" fill="${color}"/>
        <path opacity="0.1" d="M27.9977 3.5C34.9977 3.5 41.1227 6.475 45.6727 11.025L48.1227 8.4C42.8727 3.325 35.8727 0 27.9977 0C20.2977 0 13.2977 3.15 8.22266 8.225L10.6727 10.675C15.0477 6.3 21.1727 3.5 27.9977 3.5Z" fill="${color}"/>
        <path opacity="0.3" d="M44.4523 12.25C40.2523 8.05 34.4773 5.25 28.0023 5.25C21.7023 5.25 16.1023 7.875 11.9023 11.9L14.3523 14.35C17.8523 10.85 22.7523 8.75 28.0023 8.75C33.6023 8.75 38.5023 11.025 42.0023 14.875L44.4523 12.25Z" fill="${color}"/>
        <path opacity="0.5" d="M15.5781 15.575L18.0281 18.025C20.6531 15.575 24.1531 14 28.0031 14C32.2031 14 35.8781 15.75 38.5031 18.725L40.9531 16.1C37.6281 12.6 33.0781 10.5 28.0031 10.5C23.1031 10.5 18.7281 12.425 15.5781 15.575Z" fill="${color}"/>
        <path opacity="0.1" d="M55.2977 21.6992L51.9727 22.7492C52.3227 24.4992 52.4977 26.2492 52.4977 27.9992C52.4977 39.1992 44.7977 48.8242 34.4727 51.6242L35.3477 54.9492C47.2477 51.7992 55.9977 40.9492 55.9977 27.9992C55.9977 25.8992 55.6477 23.7992 55.2977 21.6992Z" fill="${color}"/>
        <path opacity="0.1" d="M3.5 28.0008C3.5 26.0758 3.85 24.1508 4.2 22.2258L0.875 21.1758C0.35 23.4508 0 25.7258 0 28.0008C0 40.9508 8.75 51.8008 20.65 54.9508L21.525 51.6258C11.2 48.8258 3.5 39.2008 3.5 28.0008Z" fill="${color}"/>
        <path opacity="0.3" d="M50.75 27.9994C50.75 26.4244 50.575 24.6744 50.225 23.2744L46.9 24.3244C47.075 25.5494 47.25 26.7744 47.25 28.1744C47.25 36.9244 41.3 44.4494 33.25 46.7244L34.125 50.0494C43.575 47.2494 50.75 38.4994 50.75 27.9994Z" fill="${color}"/>
        <path opacity="0.3" d="M8.75 28C8.75 26.6 8.925 25.025 9.275 23.8L5.95 22.75C5.425 24.5 5.25 26.25 5.25 28C5.25 38.5 12.425 47.25 22.05 49.875L22.925 46.55C14.7 44.275 8.75 36.75 8.75 28Z" fill="${color}"/>
        <path opacity="0.5" d="M45.5023 28.0008C45.5023 26.9508 45.3273 25.7258 45.1523 24.6758L41.8273 25.7258C42.0023 26.4258 42.0023 27.3008 42.0023 28.0008C42.0023 34.3008 37.8023 39.7258 32.0273 41.4758L32.9023 44.8008C40.0773 42.7008 45.5023 36.0508 45.5023 28.0008Z" fill="${color}"/>
        <path opacity="0.5" d="M14 27.9994C14 27.1244 14.175 26.0744 14.35 25.1994L11.025 24.1494C10.675 25.3744 10.5 26.7744 10.5 27.9994C10.5 36.0494 15.925 42.6994 23.275 44.7994L24.15 41.4744C18.2 39.7244 14 34.2994 14 27.9994Z" fill="${color}"/>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const clusterStyles = [
    { textColor: "white", url: generateClusterIcon("#8079CC", 72), height: 72, width: 72, textSize: 12, fontWeight: "bold" },
    { textColor: "white", url: generateClusterIcon("#1295CD", 84), height: 84, width: 84, textSize: 14, fontWeight: "bold" },
    { textColor: "white", url: generateClusterIcon("#196DBB", 96), height: 96, width: 96, textSize: 16, fontWeight: "bold" },
    { textColor: "white", url: generateClusterIcon("#084B67", 108), height: 108, width: 108, textSize: 18, fontWeight: "bold" }
  ];

  const handleCustMarkerClick = (vid) => {
    let action = global.EE.EVENT_TYPES.SUBSCRIBE_TO_VEHICLES;
    let unSubAction = global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES;
    let unsubArr = [];

    if (subscribedVeh.current === vid) return;

    if (subscribedVeh.current) unsubArr.push(subscribedVeh.current);
    subscribedVeh.current = vid;

    let vehicles = { veh: [vid] };
    if (global.UWS && global.UWS.wsConnected) {
      global.UWS.sendRequest({ action: action, data: vehicles });
      if (unsubArr.length) global.UWS.sendRequest({ action: unSubAction, data: { veh: unsubArr } });
    }
  }

  const handleCloseAndUnsub = () => {
    if (subscribedVeh.current) {
      global.UWS.sendRequest({ action: global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES, data: { veh: [subscribedVeh.current] } });
      subscribedVeh.current = null;
    }
    setShowVehicleZoom(false);
  }

  return (
    <div className={`vehicle-maps ${mapContainerClass || ""}`}>
      {isLoaded ? (
        <GoogleMap
          mapContainerClassName={`map-container ${cachedVehicleMap.size ? "" : "hide-map"}`}
          center={mapCenter}
          zoom={14}
          onLoad={onLoad}
          onZoomChanged={onZoomChanged}
          onIdle={updateClusterMaxZoom}
          onMapTypeIdChanged={updateClusterMaxZoom}
          onClick={() => setShowInfoWindow(false)}
          options={{ gestureHandling: "cooperative", streetViewControl: false }}
        >
          {(showExistingGfsToggle && !isSellerOrDealer) ? <div className="geofences-toggle-btn">
            <Checkbox onChange={(e) => setShowExistingGeofences(e.target.checked)} checked={showExistingGeofences} className="view-existing-geofences">
              View Geofences
            </Checkbox>
          </div> : null}

          {(showVehicleZoom && showVehicleZoom.vid && cachedVehicleMap.size > 1) ? <Tooltip label={isVehicleFocused ? "Zoom Out" : "Zoom In"}>
            <Button className={`focus-vehicle-btn ${isVehicleFocused ? " add-padding" : ""}`} onClick={handleVehicleZoomIn}>
              {isVehicleFocused ? <ZoomOutIcon /> : <TargetIcon />}
            </Button>
          </Tooltip> : null}

          <MarkerClusterer
            averageCenter={true}
            gridSize={80}
            maxZoom={clusterMaxZoom}
            options={{
              styles: clusterStyles,
              calculator: (markers, numStyles) => {
                const count = markers.length;
                let index = Math.min(count >= 1000 ? 4 : count >= 100 ? 3 : count >= 10 ? 2 : 1, numStyles);
                return { text: count.toString(), index: index, };
              }
            }}
          >
            {(clusterer) =>
              Array.from(cachedVehicleMap.values()).map((vehicle, idx) => {
                if (!vehicle || !vehicle.vid || !vehicle.lat) return "";
                return <CustomMarker
                  key={vehicle.vid}
                  vehicle={vehicle}
                  vehicleInfo={vehMap.get(vehicle.vid)}
                  onInfoWindowClose={() => handleCloseAndUnsub()}
                  clusterer={clusterer}
                />
              })
            }
          </MarkerClusterer>

          {showExistingGeofences ? allGeofences.map((geofence, idx) => (
            <React.Fragment key={idx}>
              <Circle
                radius={geofence.r}
                center={{ lat: geofence.clat, lng: geofence.clong }}
                editable={false}
                options={{ fillColor: "#00A8E270", strokeWeight: 2, strokeColor: "#00A8E2", strokeOpacity: 0.8, }}
                zIndex={1}
                onClick={() => setShowInfoWindow({ idx: geofence.idx })}
              />
              {showInfoWindow.idx === geofence.idx ? <InfoWindow onCloseClick={() => setShowInfoWindow(false)} options={{ pixelOffset: new window.google.maps.Size(0, -30) }} position={{ lat: geofence.clat, lng: geofence.clong }}>
                <div className="geofence-info-box">{geofence.title}</div>
              </InfoWindow> : null}
              {hideGeofenceMarkers ? null : <Marker position={{ lat: geofence.clat, lng: geofence.clong }} icon={{ ...GEOFENCE_MARKER, scaledSize: new window.google.maps.Size(30, 40), scale: 1 }} ></Marker>}
            </React.Fragment>
          )) : null}
        </GoogleMap>
      ) : null}
      <p className="map-loading">Waiting for data...</p>

      {/* <div className="scroll-overlay"></div> */}
    </div>
  );
};

export default VehicleMap;
